import { useRef, useState } from 'react';
import { Loader2, MessageSquareText, Send, Sparkles } from 'lucide-react';
import { CoachContext, askCoach } from '../services/aiCoach';
import { CoachMessage, CoachResponse } from '../types';

interface CoachChatProps {
  context: CoachContext;
  intro: string | null;
  onCoachUpdate: (coach: CoachResponse) => void;
  /** Demo mode: talk to the coach without a signed-in session. */
  demo?: boolean;
}

export function CoachChat({ context, intro, onCoachUpdate, demo = false }: CoachChatProps) {
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const nextMessages: CoachMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    setError(null);

    try {
      const coach = await askCoach(context, nextMessages, demo);
      setMessages([...nextMessages, { role: 'assistant', content: coach.reply }]);
      onCoachUpdate(coach);
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      });
    } catch (err) {
      console.error('Coach chat error:', err);
      setError('The coach could not respond right now. Please try again.');
      setMessages(messages); // roll back the optimistic user message
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const suggestions = [
    'I only have 20 minutes today',
    'I feel great — can I do more?',
    'My legs are really sore',
  ];

  return (
    <div className="card p-6">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
          <MessageSquareText className="h-4 w-4" />
        </span>
        <h3 className="text-base font-semibold text-zinc-900 dark:text-white">Talk to your coach</h3>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-indigo-600/10 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300">
          <Sparkles className="h-3 w-3" />
          AI
        </span>
      </div>

      <div ref={scrollRef} className="mt-4 max-h-72 space-y-3 overflow-y-auto">
        {intro && (
          <div className="rounded-xl rounded-tl-sm bg-zinc-100 px-4 py-2.5 text-sm leading-relaxed text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
            {intro}
          </div>
        )}
        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="ml-10 rounded-xl rounded-tr-sm bg-indigo-600 px-4 py-2.5 text-sm leading-relaxed text-white">
              {m.content}
            </div>
          ) : (
            <div key={i} className="mr-10 rounded-xl rounded-tl-sm bg-zinc-100 px-4 py-2.5 text-sm leading-relaxed text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              {m.content}
            </div>
          )
        )}
        {sending && (
          <div className="mr-10 flex items-center gap-2 rounded-xl rounded-tl-sm bg-zinc-100 px-4 py-2.5 text-sm text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Thinking about your day…
          </div>
        )}
      </div>

      {error && (
        <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">{error}</p>
      )}

      {messages.length === 0 && !sending && (
        <div className="mt-4 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setInput(s)}
              className="chip chip-off text-xs"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={send} className="mt-4 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="input flex-1"
          placeholder="Adjust today's workout — time, energy, aches…"
          disabled={sending}
        />
        <button type="submit" disabled={sending || !input.trim()} className="btn-primary px-3.5" aria-label="Send">
          <Send className="h-4 w-4" />
        </button>
      </form>
      <p className="mt-2 text-[11px] leading-snug text-zinc-400">
        The coach adjusts today&apos;s plan from your live data. Not medical advice.
      </p>
    </div>
  );
}
